import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { toast } from 'react-toastify';

import { Box, Card, CardContent, Typography, Button, Alert, CircularProgress, Divider, Grid, Chip } from '@mui/material';
import { ArrowBack, CreditCard } from '@mui/icons-material';
import { AppDispatch } from '../redux/store/store';
import { createPaymentIntent, clearError, clearClientSecret, setProcessingPayment } from '../redux/billing/billingSlice';
import { selectClientSecret, selectIsProcessingPayment, selectBillingError } from '../redux/billing/selectors';
import type { ICreditPackage } from '../types/billing';
import appConfigData from '../appConfig.json';

interface PaymentFormProps {
  creditPackage: ICreditPackage;
  onSuccess: () => void;
  onCancel: () => void;
}

const PaymentForm: React.FC<PaymentFormProps> = ({ creditPackage, onSuccess, onCancel }) => {
  const dispatch = useDispatch<AppDispatch>();
  const stripe = useStripe();
  const elements = useElements();

  const clientSecret = useSelector(selectClientSecret);
  const isProcessing = useSelector(selectIsProcessingPayment);
  const error = useSelector(selectBillingError);
  const isLoading = useSelector((state: any) => state.billing.isLoading);

  const [cardComplete, setCardComplete] = useState(false);
  const [cardError, setCardError] = useState<string | null>(null);
  const [hasInitialized, setHasInitialized] = useState(false);

  // Check if we're in test mode by looking at the Stripe publishable key from config
  const isTestMode = (appConfigData as any).STRIPE_PUBLISHABLE_KEY?.startsWith('pk_test_') || false;

  useEffect(() => {
    // Only create payment intent once, and only if not already loading/initialized
    if (!hasInitialized && !isLoading && !clientSecret && !error) {
      setHasInitialized(true);
      dispatch(createPaymentIntent(creditPackage.id));
    }

    return () => {
      // Clear client secret when component unmounts
      dispatch(clearClientSecret());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        dispatch(clearError());
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, dispatch]);

  const handleCardChange = (event: any) => {
    setCardComplete(event.complete);
    setCardError(event.error ? event.error.message : null);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements || !clientSecret) {
      return;
    }

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      return;
    }

    dispatch(setProcessingPayment(true));

    // Confirm the payment
    const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
      payment_method: {
        card: cardElement,
      },
    });

    if (confirmError) {
      setCardError(confirmError.message || 'Payment failed');
      dispatch(setProcessingPayment(false));
      return;
    }

    if (paymentIntent && paymentIntent.status === 'succeeded') {
      dispatch(setProcessingPayment(false));
      
      // Check if this is a test payment
      const isTestPayment = paymentIntent.id.startsWith('pi_test_') || !paymentIntent.livemode;
      
      if (isTestPayment) {
        toast.success(
          'Test payment successful using test card 4242 4242 4242 4242! The test credit granted to your account will appear in your balance shortly.',
          { autoClose: 8000 }
        );
      } else {
        toast.success(
          `Payment successful! $${creditPackage.price} in credits has been added to your account.`,
          { autoClose: 5000 }
        );
      }
      
      onSuccess();
      return;
    }

    dispatch(setProcessingPayment(false));
  };

  const cardElementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: '#424770',
        '::placeholder': {
          color: '#aab7c4',
        },
      },
      invalid: {
        color: '#9e2146',
      },
    },
  };

  return (
    <Box maxWidth='600px' mx='auto'>
      <Button startIcon={<ArrowBack />} onClick={onCancel} sx={{ mb: 3 }}>
        Back
      </Button>

      <Card>
        <CardContent sx={{ p: 4 }}>
          <Typography variant='h5' gutterBottom>
            Complete Your Purchase
          </Typography>

          <Box mb={3}>
            <Grid container spacing={2}>
              <Grid item xs={8}>
                <Typography variant='h6'>{creditPackage.name}</Typography>
                <Typography variant='body2' color='text.secondary'>
                  {creditPackage.description}
                </Typography>
              </Grid>
              <Grid item xs={4} textAlign='right'>
                <Typography variant='h6'>${creditPackage.price}</Typography>
              </Grid>
            </Grid>
          </Box>

          <Divider sx={{ my: 3 }} />

          {error && (
            <Alert severity='error' sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {cardError && (
            <Alert severity='error' sx={{ mb: 3 }}>
              {cardError}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Typography variant='subtitle1' gutterBottom>
              Payment Information
            </Typography>

            {isTestMode && (
              <Alert severity='info' sx={{ mb: 3 }}>
                <Box display='flex' alignItems='center' gap={1} mb={1}>
                  <CreditCard fontSize='small' />
                  <Typography variant='subtitle2'>Test Mode - Use Test Card</Typography>
                  <Chip label='TEST' size='small' color='primary' />
                </Box>
                <Typography variant='body2' sx={{ mb: 1 }}>
                  Use this test card number for testing payments:
                </Typography>
                <Typography variant='body2' sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                  4242 4242 4242 4242
                </Typography>
                <Typography variant='caption' color='text.secondary'>
                  Use any future expiry date, any 3-digit CVC, and any postal code.
                </Typography>
              </Alert>
            )}

            <Box
              sx={{
                border: 1,
                borderColor: cardError ? 'error.main' : 'divider',
                borderRadius: 1,
                p: 2,
                mb: 3,
                '& .StripeElement': {
                  width: '100%',
                },
              }}
            >
              <CardElement options={cardElementOptions} onChange={handleCardChange} />
            </Box>

            <Typography variant='body2' color='text.secondary' sx={{ mb: 3 }}>
              Your payment information is secure and encrypted. We use Stripe to process payments.
            </Typography>

            <Button
              type='submit'
              variant='contained'
              fullWidth
              size='large'
              disabled={!stripe || !cardComplete || isProcessing || !clientSecret}
              sx={{ mb: 2 }}
            >
              {isProcessing ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  Processing...
                </>
              ) : (
                `Pay $${creditPackage.price}`
              )}
            </Button>

            <Button variant='outlined' fullWidth onClick={onCancel} disabled={isProcessing}>
              Cancel
            </Button>
          </form>

          <Typography variant='caption' color='text.secondary' sx={{ mt: 2, display: 'block' }}>
            Your payment information is processed securely by Stripe.
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};

export default PaymentForm;
